import { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Eye, FileText, Filter, Search, TrendingUp, MoreVertical, FileDown } from 'lucide-react';
import {
  contratoService,
  Contrato,
  ContratoFilters,
  MRRResponse,
  formatarMoeda,
  formatarData,
  getStatusColor,
  getStatusLabel
} from '../../../../services/contratoService';
import { PublicarPortalButton } from '../../../../components/PublicarPortalButton';

interface ContratosPageProps {
  onEdit: (id: string) => void;
  onNew: () => void;
  onPreview: (id: string) => void;
  clients: Array<{ id: string; razaoSocial: string; nomeFantasia?: string | null }>;
}

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'rascunho', label: 'Rascunho' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'cancelado', label: 'Cancelado' },
  { value: 'finalizado', label: 'Finalizado' },
];

export function ContratosPage({ onEdit, onNew, onPreview, clients }: ContratosPageProps) {
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [loading, setLoading] = useState(true);
  const [mrr, setMrr] = useState<MRRResponse | null>(null);
  const [filters, setFilters] = useState<ContratoFilters>({
    status: '',
    clientId: '',
    search: '',
    page: 1,
    limit: 20,
  });
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [menuAberto, setMenuAberto] = useState<{ id: string; top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuAberto(null);
      }
    }
    if (menuAberto) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuAberto]);

  useEffect(() => {
    carregarContratos();
    carregarMRR();
  }, [filters]);

  async function carregarContratos() {
    try {
      setLoading(true);
      const response = await contratoService.list(filters);
      setContratos(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Erro ao carregar contratos:', error);
    } finally {
      setLoading(false);
    }
  }

  async function carregarMRR() {
    try {
      const data = await contratoService.getMRR();
      setMrr(data);
    } catch (error) {
      console.error('Erro ao carregar MRR:', error);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Tem certeza que deseja excluir este contrato?')) return;

    try {
      await contratoService.delete(id);
      carregarContratos();
      carregarMRR();
    } catch (error: any) {
      console.error('Erro ao excluir contrato:', error);
      alert(error.response?.data?.error || 'Erro ao excluir contrato');
    }
  }

  async function handleStatusChange(id: string, novoStatus: string) {
    try {
      await contratoService.updateStatus(id, novoStatus);
      carregarContratos();
      carregarMRR();
      setMenuAberto(null);
    } catch (error: any) {
      console.error('Erro ao alterar status:', error);
      alert(error.response?.data?.error || 'Erro ao alterar status');
    }
  }

  return (
    <div className="space-y-6">
      {/* Header com MRR */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Contratos</h2>
          <p className="text-muted-foreground">Gerencie contratos recorrentes com seus clientes</p>
        </div>
        <div className="flex items-center gap-4">
          {mrr && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                <div>
                  <p className="text-xs text-green-600 font-medium">MRR</p>
                  <p className="text-lg font-bold text-green-700">{formatarMoeda(mrr.mrr)}</p>
                </div>
              </div>
              <p className="text-xs text-green-600 mt-1">{mrr.quantidadeContratos} contratos ativos</p>
            </div>
          )}
          <button
            onClick={onNew}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white rounded-[8px] shadow-[0_4px_12px_-2px_rgba(37,99,235,0.35)] hover:shadow-[0_8px_18px_-4px_rgba(37,99,235,0.45)] hover:-translate-y-0.5 transition-all duration-200"
          >
            <Plus className="w-4 h-4" />
            Novo Contrato
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-card rounded-lg shadow-sm">
        <Filter className="w-5 h-5 text-muted-foreground" />

        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por título..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>

        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
          className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground bg-card"
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <select
          value={filters.clientId}
          onChange={(e) => setFilters({ ...filters, clientId: e.target.value, page: 1 })}
          className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground bg-card"
        >
          <option value="">Todos os clientes</option>
          {(clients || []).map(client => (
            <option key={client.id} value={client.id}>
              {client.nomeFantasia || client.razaoSocial}
            </option>
          ))}
        </select>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : contratos.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-lg shadow-sm">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Nenhum contrato encontrado</h3>
          <p className="text-muted-foreground mb-4">Crie seu primeiro contrato recorrente</p>
          <button
            onClick={onNew}
            className="btn btn-secondary btn-md"
          >
            Criar Contrato
          </button>
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow-sm">
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider rounded-tl-lg">
                  Cliente / Título
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Valor Mensal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Período
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Vencimento
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider rounded-tr-lg">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {contratos.map((contrato) => (
                <tr key={contrato.id} className="hover:bg-muted/50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-foreground">{contrato.titulo}</p>
                      <p className="text-sm text-muted-foreground">
                        {contrato.client.nomeFantasia || contrato.client.razaoSocial}
                      </p>
                      {contrato.numero && (
                        <p className="text-xs font-mono text-muted-foreground/70 mt-0.5">
                          Nº {contrato.numero}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(contrato.status)}`}>
                      {getStatusLabel(contrato.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-foreground">
                    {contrato.valorMensal != null ? formatarMoeda(Number(contrato.valorMensal)) : <span className="text-muted-foreground text-sm">A definir</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    <div>
                      <p>Início: {formatarData(contrato.dataInicio)}</p>
                      <p>Fim: {contrato.dataFim ? formatarData(contrato.dataFim) : 'Indeterminado'}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    Dia {contrato.diaVencimento}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onPreview(contrato.id)}
                        className="text-muted-foreground hover:text-foreground"
                        title="Visualizar"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {(contrato.status === 'rascunho' || contrato.status === 'ativo') && (
                        <button
                          onClick={() => onEdit(contrato.id)}
                          className="text-primary hover:text-primary/80"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      
                      {/* Gerar PDF */}
                      <button
                        onClick={async () => {
                          try {
                            await contratoService.downloadPdf(contrato.id, contrato.numero || 'contrato');
                          } catch {
                            alert('Erro ao gerar PDF');
                          }
                        }}
                        className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700"
                        title="Gerar PDF"
                      >
                        <FileDown className="w-4 h-4" />
                      </button>
                      
                      {/* Publicar no Portal */}
                      <PublicarPortalButton
                        tipo="contrato"
                        documentoId={contrato.id}
                        variant="icon"
                        publicado={contrato.publicadoPortal}
                        onSuccess={carregarContratos}
                      />

                      {/* Menu de ações */}
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            if (menuAberto?.id === contrato.id) {
                              setMenuAberto(null);
                              return;
                            }
                            const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                            setMenuAberto({
                              id: contrato.id,
                              top: rect.bottom + window.scrollY + 4,
                              right: window.innerWidth - rect.right,
                            });
                          }}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>

          {/* Paginação */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Mostrando {contratos.length} de {pagination.total} contratos
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilters({ ...filters, page: (filters.page || 1) - 1 })}
                  disabled={filters.page === 1}
                  className="px-3 py-1 border rounded hover:bg-muted/50 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setFilters({ ...filters, page: (filters.page || 1) + 1 })}
                  disabled={filters.page === pagination.totalPages}
                  className="px-3 py-1 border rounded hover:bg-muted/50 disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Menu de ações — portal fixo, escapa de qualquer overflow */}
      {menuAberto && (() => {
        const contrato = contratos.find(c => c.id === menuAberto.id);
        if (!contrato) return null;
        return (
          <div
            ref={menuRef}
            style={{ position: 'fixed', top: menuAberto.top, right: menuAberto.right, zIndex: 9999 }}
            className="w-48 bg-card rounded-lg shadow-lg border"
          >
            <div className="py-1">
              {contrato.status === 'rascunho' && (
                <button
                  onClick={() => handleStatusChange(contrato.id, 'ativo')}
                  className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-muted/50"
                >
                  Ativar Contrato
                </button>
              )}
              {contrato.status === 'ativo' && (
                <>
                  <button
                    onClick={() => handleStatusChange(contrato.id, 'cancelado')}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-muted/50"
                  >
                    Cancelar Contrato
                  </button>
                  <button
                    onClick={() => handleStatusChange(contrato.id, 'finalizado')}
                    className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-muted/50"
                  >
                    Finalizar Contrato
                  </button>
                </>
              )}
              {contrato.status === 'cancelado' && (
                <button
                  onClick={() => handleStatusChange(contrato.id, 'rascunho')}
                  className="w-full px-4 py-2 text-left text-sm text-yellow-600 hover:bg-muted/50"
                >
                  Reabrir como Rascunho
                </button>
              )}
              {(contrato.status === 'rascunho' || contrato.status === 'cancelado') && (
                <button
                  onClick={() => handleDelete(contrato.id)}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-muted/50"
                >
                  Excluir
                </button>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
